import {
  addUserToProject,
  createProject,
  deleteUserFromProject,
  fetchProject,
  fetchProjects
} from "@/api/craiyon";
import { DateTime } from "luxon";
import Vue from "vue";
import { normalize, Project, User } from "@/store/helper";

const MODULE = "project/";
const _SET_PROJECTS = "SET_PROJECTS";
const _SET_CURRENT_PROJECT = "SET_CURRENT_PROJECT";
const _LOAD_PROJECTS = "LOAD_PROJECTS";
const _CREATE_PROJECT = "CREATE_PROJECT";
const _ADD_PROJECT = "ADD_PROJECT";
const _TOTAL = "TOTAL";
const _TOGGLE_LOADING = "LOADING";
const _SET_ANNOTATION_CLASS = "SET_ANNOTATION_CLASS";
const _FIND = "FIND";
const _LIST = "LIST";
const _ACTION_SET_CURRENT_PROJECT = "_ACTION_SET_CURRENT_PROJECT";
const _ADD_USER_TO_PROJECT = "_ADD_USER_TO_PROJECT";
const _REMOVE_USER_FROM_PROJECT = "_REMOVE_USER_FROM_PROJECT";
const _MY_PROJECTS = "_MY_PROJECTS";

export const MY_PROJECTS = MODULE + _MY_PROJECTS;
export const ADD_USER_TO_PROJECT = MODULE + _ADD_USER_TO_PROJECT;
export const REMOVE_USER_FROM_PROJECT = MODULE + _REMOVE_USER_FROM_PROJECT;
export const ACTION_SET_CURRENT_PROJECT = MODULE + _ACTION_SET_CURRENT_PROJECT;
export const LIST = MODULE + _LIST;
export const FIND = MODULE + _FIND;
export const SET_PROJECTS = MODULE + _SET_PROJECTS;
export const SET_CURRENT_PROJECT = MODULE + _SET_CURRENT_PROJECT;
export const LOAD_PROJECTS = MODULE + _LOAD_PROJECTS;
export const CREATE_PROJECT = MODULE + _CREATE_PROJECT;
export const ADD_PROJECT = MODULE + _ADD_PROJECT;
export const TOTAL = MODULE + _TOTAL;
export const SET_ANNOTATION_CLASS = MODULE + _SET_ANNOTATION_CLASS;

const state = {
  byId: {},
  allIds: undefined,
  projects: null,
  currentProject: null,
  loading: false,
  cached: false
};

const getters = {
  [_TOTAL](state) {
    if (!state.projects) return 0;
    return state.projects.count;
  },
  [_FIND](state) {
    return id => {
      return state.byId[id];
    };
  },
  [_LIST](state, getters) {
    if (state.allIds === undefined) return undefined;
    return state.allIds.map(id => getters[_FIND](id));
  }
};

const mutations = {
  [_SET_PROJECTS](state, { projects }) {
    state.projects = projects;
  },
  [_SET_CURRENT_PROJECT](state, { project }) {
    state.currentProject = project;
  },
  [_ADD_PROJECT](state, { project }) {
    state.projects.results.push(project);
  },
  [_TOGGLE_LOADING](state) {
    state.loading = !state.loading;
  },
  [_SET_ANNOTATION_CLASS](state, annotationClass) {
    Vue.set(state.currentProject, "annotation_class", annotationClass);
  },
  add: (state, item) => {
    if (state.allIds === undefined) {
      state.allIds = [];
    }

    for (let key of Object.keys(item)) {
      Vue.set(state.byId, key, item[key]);
      if (state.allIds.includes(key)) return;
      state.allIds.push(key);
    }
  },
  addUser: (state, userId) => {
    state.currentProject.allowed_userlist.push(userId);
  },
  removeUser: (state, userId) => {
    state.currentProject.allowed_userlist = state.currentProject.allowed_userlist.filter(
      e => e !== userId
    );
  },
  setCached: (state, value) => (state.cached = value)
};

const actions = {
  async [_LOAD_PROJECTS]({ commit, rootGetters, state }, paging) {
    // TODO some hooks to only load data if needed
    // if (paging && state.allIds) {
    //   if (state.allIds.length > paging.itemsPerPage) {
    //     return;
    //   }
    //   if (state.projects && state.projects.count <= paging.itemsPerPage) return;
    // }

    if (state.cached) return;

    commit(_TOGGLE_LOADING);
    commit(_SET_PROJECTS, { projects: [] });

    const projects = await fetchProjects(rootGetters.token, paging);

    projects.results.forEach(e => {
      let r = normalize(e, Project);
      console.log(r);
      commit("add", r.entities.project);
      commit("user/add", r.entities.user, { root: true });
    });

    commit("setCached", true);
    // commit(_SET_PROJECTS, { projects });
    commit(_TOGGLE_LOADING);
  },
  async [_CREATE_PROJECT]({ commit, rootGetters }, model) {
    commit(_TOGGLE_LOADING);
    const res = await createProject(rootGetters.token, model);
    const project = {
      project_name: model.project_name,
      details: model.details,
      created: DateTime.local().toISO(),
      last_updated: DateTime.local().toISO(),
      status: "started",
      owner: { username: rootGetters.tokenParsed.email },
      id: res.id
    };

    let r = normalize(project, Project);
    commit("add", r.entities.project);
    commit("user/add", r.entities.user, { root: true });
    // commit(_ADD_PROJECT, { project });
    commit(_TOGGLE_LOADING);
  },
  async [_ACTION_SET_CURRENT_PROJECT](
    { commit, state, rootGetters },
    { project, id }
  ) {
    commit(_TOGGLE_LOADING);
    let projectRetrieved = project;
    if (!project) {
      projectRetrieved = await fetchProject(rootGetters.token, id);
      let r = normalize(projectRetrieved, Project);
      // projectRetrieved = r.entities.project[r.result];
      commit("add", r.entities.project);
      commit("user/add", r.entities.user, { root: true });
    } else {
      if (state.currentProject && state.currentProject.id === project.id) {
        commit(_TOGGLE_LOADING);
        return;
      }
    }

    commit("annotationClass/reset", null, { root: true });
    commit("slides/reset", null, { root: true });

    commit(_SET_CURRENT_PROJECT, { project: projectRetrieved });
    commit(_TOGGLE_LOADING);
  },
  async [_ADD_USER_TO_PROJECT]({ commit, rootGetters, state }, { user }) {
    const token = rootGetters.token;
    await addUserToProject(token, user.id, state.currentProject);
    let r = normalize(user, User);
    commit("user/add", r.entities.user, { root: true });
    commit("addUser", r.result);
  },
  async [_REMOVE_USER_FROM_PROJECT]({ commit, rootGetters, state }, { user }) {
    const token = rootGetters.token;
    await deleteUserFromProject(token, user.id, state.currentProject);
    commit("removeUser", user.id);
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
};
